import { Measure } from '@cbsi-cbscom/measure';
import { VideoPlayerInterface } from '@cbsinteractive/avia-js/dist/types/';
import { IAutoplayInfo } from 'library/ts/interfaces/IAutoplayInfo';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { IPlayerAdapterDeviceCapabilities } from 'library/ts/player/adapter/interfaces/capabilities/IPlayerAdapterDeviceCapabilities';
import { IResourceConfig } from 'library/ts/player/adapter/interfaces/resource/config/IResourceConfig';
import { IPlayerAdapter } from 'library/ts/player/adapter/IPlayerAdapter';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagEventDataVO } from 'tag/ts/tag/interfaces/events/ITagEventDataVO';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { AbstractAdapter } from 'tag/ts/tag/player/adapters/abstract/AbstractAdapter';
import { AviaFactory } from 'tag/ts/tag/player/adapters/avia/AviaFactory';
import { IAviaPlayerAdapterAPI } from 'tag/ts/tag/player/adapters/avia/interfaces/adapter/IAviaPlayerAdapterAPI';
import { AviaCapabilitiesManager } from 'tag/ts/tag/player/adapters/avia/managers/AviaCapabilitiesManager';
import { AviaDependencyManager } from 'tag/ts/tag/player/adapters/avia/managers/AviaDependencyManager';
import { AviaLifecycleManager } from 'tag/ts/tag/player/adapters/avia/managers/AviaLifecycleManager';
import { INerdModeManager } from 'tag/ts/tag/interfaces/nerdmode/INerdModeManager';
import { AviaPlayerOptionsManager } from 'tag/ts/tag/player/adapters/avia/resources/AviaPlayerOptionsManager';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Main VTG Avia player adapter orchestrating Smart Tag integration
 *
 * **Primary Integration Controller:**
 * Serves as the main orchestration layer between Smart Tag and VTG Avia player technology.
 * Coordinates all VTG integration components including factory, wrapper, transformers,
 * plugins, and resource management to provide unified video playback functionality.
 *
 * **Component Orchestration:**
 * - **AviaFactory**: Creates and destroys VTG player instances with proper configuration
 * - **AviaPlayerOptionsManager**: Manages platform-specific player configuration
 * - **AviaCapabilitiesManager**: Handles device capability detection and autoplay testing
 * - **AviaDependencyManager**: Manages Avia script loading and dependency orchestration
 * - **AviaLifecycleManager**: Manages player lifecycle, wrapper, and Nerd Mode integration
 * - **Plugin Ecosystem**: Coordinates video decode, ad integration, and tracking plugins
 * - **Resource Management**: Handles content configuration and streaming setup
 *
 * **Adapter Pattern Implementation:**
 * Implements the Adapter pattern to make VTG's Avia player compatible with Smart Tag's
 * player interface, allowing Smart Tag to use VTG technology without tight coupling.
 *
 * **Lifecycle Management:**
 * - **Player Creation**: Atomic player replacement delegated to AviaLifecycleManager
 * - **Device Capabilities**: VTG device capability detection via AviaCapabilitiesManager
 * - **Resource Configuration**: Content-specific player setup and optimization
 * - **Error Handling**: Graceful failure management and recovery workflows
 * - **Cleanup**: Complete teardown of VTG resources and Smart Tag integration
 *
 * **Business Impact:**
 * Enables Smart Tag to leverage VTG's video streaming technology while maintaining
 * Smart Tag's business logic, component architecture, and platform flexibility.
 */
export class AviaAdapter extends AbstractAdapter implements IPlayerAdapter {

	// placeholder for player factory class instance
	#aviaFactory: AviaFactory;
	// placeholder  for avia player options manager
	#internalPlayerOptionsManager: AviaPlayerOptionsManager;
	// capabilities manager for device detection and autoplay testing
	#capabilitiesManager: AviaCapabilitiesManager;
	// dependency manager for Avia script loading
	#dependencyManager: AviaDependencyManager;
	// lifecycle manager for player creation, destruction, and wrapper management
	#lifecycleManager: AviaLifecycleManager;

	// ========================================================================
	// 🎯 PUBLIC API: NERD MODE
	// ========================================================================

	/**
	 * @description Returns the live Nerd Mode manager instance, or null if the plugin is
	 * disabled or not yet initialized (no player created yet). Callers (e.g. `TagAPI`) use
	 * this single accessor to reach the full `INerdModeManager` surface — `show()`,
	 * `registerTab()`, `getLog()`, etc. — rather than one pass-through method per API call.
	 * @public
	 */
	getNerdModeManager(): INerdModeManager | null {
		return this.#lifecycleManager?.getNerdModeManager() ?? null;
	}

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaAdapter'
			}
		);

		// Initialize managers
		this.#capabilitiesManager = new AviaCapabilitiesManager( { tag: this.tag } );
		this.#dependencyManager = new AviaDependencyManager( { tag: this.tag } );
		// Note: AviaLifecycleManager is initialized AFTER AviaFactory is created in initialize()
	}

	/**
	 * @description Returns instance of adapted player API
	 * @returns {IAviaPlayerAdapterAPI}
	 */
	get player(): IAviaPlayerAdapterAPI {
		return this.#lifecycleManager?.getPlayerWrapper()?.API;
	}

	get #playerOptionsManager(): AviaPlayerOptionsManager {
		// create an instance of player options manager
		if ( this.utils.Type.isUndefined( this.#internalPlayerOptionsManager ) ) {
			this.#internalPlayerOptionsManager = new AviaPlayerOptionsManager( { tag: this.tag } );
		}
		return this.#internalPlayerOptionsManager;
	}

	/**
	 * @description Returns instance of non-adapted avia player
	 * @returns {VideoPlayerInterface}
	 */
	get adaptedPlayerInstance(): VideoPlayerInterface {
		return this.#lifecycleManager?.getPlayerWrapper()?.adaptedPlayerInstance;
	}

	/**
	 * @description Number of player instance created within current session
	 * @returns {any}
	 */
	get playerNumber() {
		return this.#lifecycleManager?.getPlayerNumber() ?? 0;
	}

	/**
	 * @description Initialize AviaAdapter class
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	initialize = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				// subscribe to Tag application events
				this.tagEventsHandler(
					this.#onTagEvent,
					[
						TagEvent.ON_CREATE_PLAYER_REQUEST,
						TagEvent.ON_DESTROY_PLAYER_REQUEST
					]
				);

				// Calculate Avia dependencies via dependency manager
				const imports = this.#dependencyManager.calculateDependencies();
				this?.logger?.log( 'initialize:', 'loadDependencies (avia)', 'imports:', imports );
				Measure.start( Metrics.aviaLoadTime );

				// Load Avia dependencies via AbstractAdapter
				this.importDependencies( imports ).then(
					( data ) => {
						this.#aviaFactory = new AviaFactory( { tag: this.tag } );

						// Initialize lifecycle manager AFTER factory is ready
						this.#lifecycleManager = new AviaLifecycleManager( {
							tag: this.tag,
							logger: this.logger,
							aviaFactory: this.#aviaFactory,
							playerOptionsManager: this.#playerOptionsManager
						} );

						this.utils.Delay.delay()
							.then(
								() => {
									Measure.end( Metrics.aviaLoadTime );
									// run STV device detection via capabilities manager
									this.#capabilitiesManager.detectDeviceCapabilities().then(
										( data ) => {
											resolve( { msg: 'Done', success: true, result: data } );
										}
									).catch(
										( error ) => {
											// Device capabilities detection failed, but adapter can still initialize with defaults
											this?.logger?.warn( 'Device capabilities detection failed, continuing with defaults:', error );
											resolve( { msg: 'Done (no device capabilities)', success: true, result: null } );
										}
									);
								}
							);
					}
				).catch(
					( error ) => {
						this?.logger?.warn( 'Error loading dependencies:', error );
						this.dispatchTagEvent(
							new TagEventVO(
								TagEvent.ON_CREATE_PLAYER_FAILURE,
								this.tag,
								{ playerNumber: this.playerNumber } as ITagEventDataVO,
								this
							)
						);
					}
				);
			}
		);
	};

	/**
	 * @description Gets autoplay capabilities using avia player
	 * Delegates to AviaCapabilitiesManager
	 * @return {Promise<{autoplayInfo: IAutoplayInfo}>}
	 */
	getAutoplayCapabilities = async (): Promise<{ autoplayInfo: IAutoplayInfo }> => {
		return this.#capabilitiesManager.getAutoplayCapabilities();
	};

	/**
	 * @description Get device capabilities
	 * Delegates to AviaCapabilitiesManager
	 * @returns {Promise<IPlayerAdapterDeviceCapabilities>}
	 */
	getDeviceCapabilities = (): Promise<IPlayerAdapterDeviceCapabilities> => {
		return this.#capabilitiesManager.getDeviceCapabilities();
	};

	/**
	 *  @description Public method for requesting player stop
	 * @returns {(event?: TagEventVO) => Promise<IPromiseResolveGeneric>}
	 */
	get requestPlayerStop() {
		return this.#lifecycleManager?.getPlayerWrapper()?.requestPlayerStop;
	}

	/**
	 * @description Returns Resource config object in a promise
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>}
	 */
	get resourceConfig(): Promise<IPromiseResolveGeneric & { resource: IResourceConfig }> {
		// 🔄 ARCHITECTURAL BOUNDARY: avia-js → Player Adapter
		// Converts internal ResourceConfigurationInterface to external IResourceConfig
		// This maintains separation between avia-js implementation and Player Adapter abstraction
		return this.#aviaFactory.getResourceConfig().then( ( result ) => ( {
			...result,
			resource: result.resource as unknown as IResourceConfig // Safe: identical underlying structure
		} ) );
	}

	/**
	 * @description Tag application event handler - delegates to lifecycle manager
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		this?.logger?.log( TagLoggerString.ON_TAG_EVENT, event?.type, event );

		// Delegate lifecycle events to AviaLifecycleManager
		switch ( event?.type ) {
			case TagEvent.ON_CREATE_PLAYER_REQUEST:
				// Validate player container before delegating
				if ( !this.utils.Type.isElement( event?.data?.playerContainer ) ) {
					this?.logger?.warn( TagLoggerString.EXECUTE_PLAYER_CREATE_EVENT, 'player container is not a valid DOM element:', event?.data?.playerContainer );
					return;
				}
				this.#lifecycleManager.createPlayer( event?.data?.playerContainer );
				break;
			case TagEvent.ON_DESTROY_PLAYER_REQUEST:
				this.#lifecycleManager.destroyPlayer( event?.data?.eventCallback );
				break;
		}
	};

}